<template>
  <section class="faq-page" :class="{ 'faq-page--dark': $vuetify.theme.dark }">
    <header class="faq-page__heading">
      <div><span>Home</span><v-icon size="16">mdi-chevron-right</v-icon><strong>FAQ</strong></div>
    </header>

    <main class="faq-panel">
      <header class="faq-panel__heading">
        <span class="faq-heading-icon"><v-icon size="29">mdi-help</v-icon></span>
        <div>
          <h2>Frequently Asked Questions</h2>
          <p>Find answers to common questions about your trading experience.</p>
        </div>
        <label class="faq-search">
          <v-icon size="22">mdi-magnify</v-icon>
          <input v-model="search" type="search" placeholder="Search questions..." />
        </label>
      </header>

      <div class="faq-list">
        <article
          v-for="(item, index) in filteredFaqs"
          :key="item.id || index"
          class="faq-item"
          :class="{ 'faq-item--open': isOpen(item, index) }"
        >
          <button type="button" class="faq-question" @click="toggle(item, index)">
            <span class="faq-number">{{ originalIndex(item) + 1 }}</span>
            <strong>{{ item.question }}</strong>
            <v-icon size="20">{{ isOpen(item, index) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </button>
          <transition name="faq-answer">
            <div v-if="isOpen(item, index)" class="faq-answer">
              <div v-html="item.answer"></div>
            </div>
          </transition>
        </article>

        <div v-if="!filteredFaqs.length" class="faq-empty">
          <v-icon size="42">mdi-help-circle-outline</v-icon>
          <strong>No matching questions</strong>
          <span>Try a different search term.</span>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { FAQS__ACTIONS } from "@/store/action-types";

export default {
  name: "FaqView",
  data: () => ({ search: "", openKey: null }),
  computed: {
    ...mapState("faqs", ["faqs"]),
    faqItems() { return Array.isArray(this.faqs) ? this.faqs : []; },
    filteredFaqs() {
      const query = this.search.trim().toLowerCase();
      if (!query) return this.faqItems;
      return this.faqItems.filter(item => `${item.question || ""} ${item.answer || ""}`.toLowerCase().includes(query));
    },
  },
  methods: {
    ...mapActions("faqs", [FAQS__ACTIONS.FAQS]),
    itemKey(item, index) { return item.id !== undefined && item.id !== null ? `id-${item.id}` : `index-${this.originalIndex(item, index)}`; },
    originalIndex(item, fallback = 0) { const index = this.faqItems.indexOf(item); return index >= 0 ? index : fallback; },
    isOpen(item, index) { return this.openKey === this.itemKey(item, index); },
    toggle(item, index) { const key = this.itemKey(item, index); this.openKey = this.openKey === key ? null : key; },
  },
  async mounted() {
    try {
      await this[FAQS__ACTIONS.FAQS]();
    } catch (error) { console.error("Failed to load FAQs", error); }
  },
};
</script>

<style scoped>
.faq-page {
  --page: #f7f8fa; --panel: #fff; --row: #fff; --border: #dfe2e6; --text: #171a1f; --muted: #626973; --primary: #d99a00;
  box-sizing: border-box; height: calc(100vh - 64px); min-height: 0; padding: 16px 18px 18px; overflow-x: hidden; overflow-y: auto; color: var(--text); background: var(--page); scrollbar-width: thin; scrollbar-color: var(--border) transparent;
}
.faq-page--dark { --page: #030b13; --panel: #07111b; --row: #0b1621; --border: #293026; --text: #f4f5f7; --muted: #aeb6c0; --primary: #ffc400; background: radial-gradient(circle at 65% 16%, rgba(255, 196, 0, .055), transparent 35%), var(--page); }
.faq-page::-webkit-scrollbar { width: 6px; }
.faq-page::-webkit-scrollbar-track { background: transparent; }
.faq-page::-webkit-scrollbar-thumb { border-radius: 10px; background: var(--border); }
.faq-page__heading { height: 34px; padding: 0 5px; }
.faq-page__heading > div { display: flex; align-items: center; gap: 7px; color: var(--muted); font-size: 12px; }
.faq-page__heading strong { color: var(--text); font-weight: 500; }
.faq-page__heading .v-icon { color: var(--muted); }
.faq-panel { min-height: calc(100vh - 160px); padding: 22px 20px; border: 1px solid var(--border); border-radius: 10px; background: var(--panel); }
.faq-panel__heading { display: grid; grid-template-columns: 46px minmax(0, 1fr) minmax(250px, 300px); align-items: center; gap: 14px; margin-bottom: 24px; }
.faq-heading-icon { width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center; color: var(--primary); border: 3px solid var(--primary); border-radius: 50%; }
.faq-heading-icon .v-icon { color: inherit; }
.faq-panel__heading h2 { margin: 0 0 3px; font-size: 19px; }
.faq-panel__heading p { margin: 0; color: var(--muted); font-size: 12px; }
.faq-search { height: 42px; display: flex; align-items: center; gap: 10px; padding: 0 13px; border: 1px solid var(--border); border-radius: 7px; background: var(--row); }
.faq-search .v-icon { color: var(--primary); }
.faq-search input { min-width: 0; width: 100%; color: var(--text); border: 0; outline: 0; background: transparent; }
.faq-search input::placeholder { color: var(--muted); }
.faq-list { display: grid; gap: 7px; }
.faq-item { overflow: hidden; border: 1px solid var(--border); border-radius: 9px; background: var(--row); transition: border-color .2s ease, background .2s ease; }
.faq-item--open { border-color: var(--primary); background: linear-gradient(115deg, rgba(217, 154, 0, .08), rgba(255, 247, 223, .32)); }
.faq-page--dark .faq-item--open { background: linear-gradient(115deg, rgba(255, 196, 0, .12), rgba(11, 22, 33, .58)); }
.faq-question { width: 100%; min-height: 54px; display: flex; align-items: center; gap: 15px; padding: 10px 18px; color: var(--text); text-align: left; border: 0; background: transparent; cursor: pointer; }
.faq-question strong { min-width: 0; flex: 1; font-size: 14px; }
.faq-question > .v-icon { color: var(--primary); }
.faq-number { width: 30px; height: 30px; display: inline-flex; flex: 0 0 30px; align-items: center; justify-content: center; color: var(--text); font-size: 13px; font-weight: 700; border-radius: 50%; background: rgba(115, 128, 158, .09); }
.faq-item--open .faq-number { color: #17130a; background: linear-gradient(135deg, #ffd43b, #ffc400); box-shadow: 0 4px 12px rgba(217, 154, 0, .22); }
.faq-answer { padding: 0 32px 20px 62px; }
.faq-answer > div { padding: 2px 18px; color: var(--muted); font-size: 12px; line-height: 1.7; border-left: 2px solid var(--primary); }
.faq-answer::v-deep p { margin: 0; }
.faq-empty { min-height: 230px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 7px; color: var(--muted); text-align: center; }
.faq-empty .v-icon { color: var(--primary); }
.faq-empty strong { color: var(--text); }
.faq-answer-enter-active, .faq-answer-leave-active { transition: opacity .18s ease, transform .18s ease; }
.faq-answer-enter, .faq-answer-leave-to { opacity: 0; transform: translateY(-5px); }

@media (max-width: 760px) {
  .faq-page { height: calc(100vh - 56px); height: calc(100dvh - 56px); padding: 13px 10px 18px; -webkit-overflow-scrolling: touch; }
  .faq-page__heading { height: 32px; }
  .faq-panel { min-height: calc(100dvh - 135px); padding: 15px 10px; }
  .faq-panel__heading { grid-template-columns: 40px minmax(0, 1fr); gap: 10px; margin-bottom: 15px; }
  .faq-heading-icon { width: 36px; height: 36px; }
  .faq-panel__heading h2 { font-size: 16px; }
  .faq-panel__heading p { font-size: 11px; }
  .faq-search { grid-column: 1 / -1; }
  .faq-question { min-height: 58px; gap: 10px; padding: 9px 11px; }
  .faq-question strong { font-size: 13px; }
  .faq-number { width: 28px; height: 28px; flex-basis: 28px; }
  .faq-answer { padding: 0 13px 16px 49px; }
  .faq-answer > div { padding: 0 12px; }
}
</style>
